import { transpose, multiply, lusolve } from 'mathjs'

// Define the type for geoPoint
interface GeoPoint {
  x: number
  y: number
  lon: number
  lat: number
}

// Define the type for item
interface Item {
  x?: number
  y?: number
  lon?: number
  lat?: number
  type?: string
  width?: number
  height?: number
  scaleX?: number
  scaleY?: number
  points?: number[]
}

// Function to solve a linear system of equations
function solveLinearSystem(A: number[][], B: number[]): number[] {
  // Transpose matrix A
  const AT = transpose(A) as number[][]
  // Compute A^T * A
  const ATA = multiply(AT, A) as number[][]
  // Compute A^T * B
  const ATB = multiply(AT, B) as number[]  // This is a vector
  // Solve the linear system
  return (lusolve(ATA, ATB) as number[][]).map(row => row[0])
}

// Function to update items with their lon and lat values
export function updateItemsLonLat(geoPoints: GeoPoint[], items: Item[]): void {
  // Extract x, y, lon, and lat arrays from geoPoints
  const x = geoPoints.map(p => p.x)
  const y = geoPoints.map(p => p.y)
  const lon = geoPoints.map(p => p.lon)
  const lat = geoPoints.map(p => p.lat)

  // Create matrix A for solving linear system for lon and lat coefficients
  const A = x.map((xi, i) => [xi, y[i], 1])
  // Solve linear system for lon and lat coefficients
  const lonCoeffs = solveLinearSystem(A, lon)
  const latCoeffs = solveLinearSystem(A, lat)

  // Update each item with its lon and lat values
  items.forEach(item => {
    let item_x = item.x || 0
    let item_y = item.y || 0

    // Update Signs to the center points
    if (item?.type === 'sign') {
      item_x += ((item?.width || 25) * (item?.scaleX || 1)) / 2
      item_y += ((item?.height || 25) * (item?.scaleY || 1)) / 2
    }
    // Update Line items to the center points
    else if (item?.type === 'line') {
      const points = item.points

      try {
        if (Array.isArray(points) && points.length > 0 && points.length % 2 === 0) {
          const xs = points.filter((_, i) => i % 2 === 0)
          const ys = points.filter((_, i) => i % 2 === 1)

          item_x = xs.reduce((sum, x) => sum + x, 0) / xs.length
          item_y = ys.reduce((sum, y) => sum + y, 0) / ys.length
        } else {
          item_x = 0
          item_y = 0
        }
      } catch (error) {
        item_x = 0
        item_y = 0
      }
    }
    // Calculate lon and lat values for the item
    item.lon = lonCoeffs[0] * (item_x || 0) + lonCoeffs[1] * (item_y || 0) + lonCoeffs[2]
    item.lat = latCoeffs[0] * (item_x || 0) + latCoeffs[1] * (item_y || 0) + latCoeffs[2]
  })
}
